// Rendering @mentions inside a message body.
//
// The composer already records WHO was mentioned (mention_user_ids, validated server-side
// against the participants). The body itself is plain text, so a mention used to read as
// ordinary words — "@Ally" looked the same as any other text and you could not tell
// whether the tag had landed. This turns the names that really were mentioned into chips,
// and marks the one that is you.
export type MessagePart =
  | { kind: 'text'; text: string }
  | { kind: 'url'; text: string }
  | { kind: 'mention'; text: string; userId: string; isSelf: boolean };

const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]}'"])/gi;

export interface MentionTarget {
  userId: string;
  /** The display name the composer inserted after "@". */
  name: string;
  isSelf: boolean;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Split a body into text, links and the mentions that actually carry a user id.
 *
 *  Only names from `targets` become chips: a bare "@someone" that the composer never
 *  resolved stays plain text, so the rendering never claims a tag that was not sent.
 *  Longer names win over shorter ones so "@Ally Mau" doesn't match as "@Ally". */
export function splitMessage(text: string, targets: MentionTarget[] = []): MessagePart[] {
  const named = targets.filter((m) => m.name.trim().length > 0)
    .sort((a, b) => b.name.length - a.name.length);
  const mentionRe = named.length
    ? new RegExp(`@(${named.map((m) => escapeRe(m.name)).join('|')})\\b`, 'gi')
    : null;

  const marks: { start: number; end: number; part: MessagePart }[] = [];
  for (const m of text.matchAll(URL_RE)) {
    const start = m.index ?? 0;
    marks.push({ start, end: start + m[0].length, part: { kind: 'url', text: m[0] } });
  }
  if (mentionRe) {
    for (const m of text.matchAll(mentionRe)) {
      const start = m.index ?? 0;
      // A mention inside a URL is part of the link, not a tag.
      if (marks.some((x) => start >= x.start && start < x.end)) continue;
      const hit = named.find((t) => t.name.toLowerCase() === m[1].toLowerCase());
      if (!hit) continue;
      marks.push({ start, end: start + m[0].length, part: { kind: 'mention', text: m[0], userId: hit.userId, isSelf: hit.isSelf } });
    }
  }
  marks.sort((a, b) => a.start - b.start);

  const out: MessagePart[] = [];
  let last = 0;
  for (const mark of marks) {
    if (mark.start < last) continue; // overlapping match — keep the first
    if (mark.start > last) out.push({ kind: 'text', text: text.slice(last, mark.start) });
    out.push(mark.part);
    last = mark.end;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  return out.length ? out : [{ kind: 'text', text }];
}

/** True when the viewer is one of the people this message tagged. */
export function mentionsMe(mentionedUserIds: string[] | undefined, selfId: string | undefined): boolean {
  return !!selfId && !!mentionedUserIds?.some((id) => id === selfId);
}
